import {keys} from '../../../api/keys';
import {getLocalData, setLocalData} from '../../../utils/storage/storage';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {tr} from '../../../translations';
import {
  ActionType,
  AnalyticsEvents,
  ArticleType,
  ToastType,
} from '../../../utils/enums';
import {analyticsService} from '../../../utils/services/analytics'; // Update the path accordingly

/**
 * Creates a new note with a random ID, title, description, and date.
 * @param {string} title - The title of the note.
 * @param {string} description - The description of the note.
 * @param {any} news - The news of the note.
 * @returns {Promise<void>} - A promise that resolves when the note is successfully created.
 */
export const createNote = async (
  title: string,
  description: string,
  news: any,
): Promise<boolean> => {
  try {
    const notes = getLocalData(keys.NOTES) || [];
    const newNote = {
      id: news?.id || (notes || []).length + 1,
      title,
      description,
      date: new Date().toISOString(),
      news,
    };
    notes.push(newNote);
    setLocalData(keys.NOTES, notes);
    const articleType = news?.isSummary
      ? ArticleType.SUMMARY
      : news?.isRecommendation || news?.isRecommendationByTab
        ? ArticleType.RECOMMENDATIONS
        : ArticleType.NEWS;
    analyticsService?.logEvent(AnalyticsEvents.NoteActions, {
      articleId: news?.id,
      articleTitle: news?.title,
      actionType: ActionType.CREATE_NOTE,
      articleType,
      newNote,
    });
    showGlobalToast({message: tr('notes.addToNotes'), type: ToastType.HELP});
    return true;
  } catch (e) {
    showGlobalToast({message: tr('app.error'), type: ToastType.ERROR});
    return false;
  }
};

/**
 * Edits an existing note by ID.
 * @param {string} id - The ID of the note to edit.
 * @param {string} title - The new title of the note.
 * @param {string} description - The new description of the note.
 * @returns {Promise<boolean>} - A promise that resolves when the note is successfully edited.
 */
export const editNote = async (
  id: string,
  title: string,
  description: string,
): Promise<boolean> => {
  const notes = getLocalData(keys.NOTES) || [];
  const noteIndex = notes.findIndex((note: any) => note.id === id);

  if (noteIndex !== -1) {
    notes[noteIndex].title = title;
    notes[noteIndex].description = description;
    notes[noteIndex].date = new Date().toISOString(); // Update date to reflect the edit time
    setLocalData(keys.NOTES, notes);
    if (notes[noteIndex].news) {
      const news = notes[noteIndex].news;
      const articleType = news?.isSummary
        ? ArticleType.SUMMARY
        : news?.isRecommendation
          ? ArticleType.RECOMMENDATIONS
          : ArticleType.NEWS;
      analyticsService?.logEvent(AnalyticsEvents.NoteActions, {
        articleId: news?.id,
        articleTitle: news?.title,
        actionType: ActionType.EDIT_NOTE,
        articleType,
        newNote: news,
      });
    }
    showGlobalToast({message: tr('notes.editNotes'), type: ToastType.HELP});
    return true;
  } else {
    return false;
  }
};

/**
 * Deletes a note by ID.
 * @param {string} id - The ID of the note to delete.
 * @returns {Promise<void>} - A promise that resolves when the note is successfully deleted.
 */
export const deleteNote = async (id: string): Promise<void> => {
  const notes = getLocalData(keys.NOTES) || [];
  const noteIndex = notes.findIndex((note: any) => note.id === id);
  const filteredNotes = notes.filter((note: any) => note.id !== id);
  setLocalData(keys.NOTES, filteredNotes);
  if (notes[noteIndex].news && noteIndex > -1) {
    const news = notes[noteIndex].news;
    const articleType = news?.isSummary
      ? ArticleType.SUMMARY
      : news?.isRecommendation
        ? ArticleType.RECOMMENDATIONS
        : ArticleType.NEWS;
    analyticsService?.logEvent(AnalyticsEvents.NoteActions, {
      articleId: news?.id,
      articleTitle: news?.title,
      actionType: ActionType.DELETE_NOTE,
      articleType,
      newNote: news,
    });
  }
  showGlobalToast({message: tr('notes.removeFromNotes'), type: ToastType.HELP});
};

/**
 * Retrieves all notes.
 * @returns {Promise<any[]>} - A promise that resolves to an array of all notes.
 */
export const getAllNotes = async (): Promise<any[]> => {
  return getLocalData(keys.NOTES) || [];
};

/**
 * Retrieves note by id.
 * @returns {Promise<any[]>} - A promise that resolves note.
 */
export const getNoteById = async (id: number): Promise<any> => {
  const notes = getLocalData(keys.NOTES) || [];
  const noteIndex = notes.findIndex((note: any) => note.id === id);

  if (noteIndex !== -1) {
    return notes[noteIndex];
  } else {
    return null;
  }
};
